'use strict';

/**
 * T315 Module Lock — P3 DELETE proof-of-knowledge.
 *
 * The server cannot see the lock secret (custodian-of-ciphertext), so it
 * cannot directly verify "you know the passphrase." Instead, removing a lock
 * requires a value only a client that UNWRAPPED the module master can compute:
 *
 *   proof = sha256(moduleMaster || saltPin)
 *
 * The expected proof is recorded server-side at setup time (alongside the
 * opaque pinWrap envelope). On DELETE the client recomputes it from the
 * just-unwrapped master and the salt; the server compares with timing-safe
 * equality. A session cookie alone cannot strip a lock.
 *
 * Pure + browser-safe (uses only the crypto shim's createHash + Buffer).
 */

const crypto = require('crypto');

function _toBytes(master) {
  if (master instanceof Uint8Array) return Buffer.from(master);
  if (Buffer.isBuffer(master)) return master;
  throw new TypeError('moduleMaster must be Uint8Array or Buffer');
}

function computeRemoveProof(moduleMaster, saltPinHex) {
  if (typeof saltPinHex !== 'string' || saltPinHex.length < 32) {
    throw new Error('saltPin must be a hex string of at least 16 bytes');
  }
  const h = crypto.createHash('sha256');
  h.update(_toBytes(moduleMaster));
  h.update(Buffer.from(saltPinHex, 'hex'));
  return h.digest('hex');
}

function verifyRemoveProof(expectedHex, suppliedHex) {
  if (typeof expectedHex !== 'string' || typeof suppliedHex !== 'string') return false;
  if (expectedHex.length !== suppliedHex.length) return false;
  try {
    return crypto.timingSafeEqual(Buffer.from(expectedHex, 'hex'), Buffer.from(suppliedHex, 'hex'));
  } catch (_e) {
    return false;
  }
}

module.exports = { computeRemoveProof, verifyRemoveProof };
